import * as styles from './customize-sidebar-modal.css';
import {
  Box,
  Card,
  Icon,
  InlineStack,
  Modal,
  ModalBody,
  type PlumaManagedModalProps,
  Select,
  Text,
  TextLabel,
} from '@customerio/pluma-components/react';
import { useEffect, useRef, useState } from 'react';
import {
  buildSideNavPreferences,
  SIDE_NAV_VISIBILITY_OPTIONS,
  type CustomizableNavItemGroup,
  type SideNavPreferences,
  type SideNavVisibility,
} from './side-nav-preferences';

type PreferenceChange = {
  itemId: string;
  section: string;
  previousValue: SideNavVisibility;
  newValue: SideNavVisibility;
};

export type CustomizeSidebarModalData = {
  environmentId?: string;
  groups: CustomizableNavItemGroup[];
  preferences: SideNavPreferences | undefined;
  onPreferenceChange: (change: PreferenceChange) => void;
  onSave: (preferences: SideNavPreferences) => Promise<SideNavPreferences>;
};

export function CustomizeSidebarModal({
  data,
  modalState,
  onClose,
}: PlumaManagedModalProps<CustomizeSidebarModalData>) {
  const { environmentId, groups, preferences, onPreferenceChange, onSave } = data;
  const [selections, setSelections] = useState<Record<string, SideNavVisibility>>({});
  const preferencesRef = useRef(preferences);
  const saveIdRef = useRef(0);
  const selectionsRef = useRef(selections);

  useEffect(() => {
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- existing effect pattern before this rule was enforced strictly; avoid adding new cases
    if (modalState.isOpen !== true) {
      return;
    }

    const initialSelections = getInitialSelections(groups);
    preferencesRef.current = preferences;
    selectionsRef.current = initialSelections;
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change -- existing effect pattern before this rule was enforced strictly; avoid adding new cases
    setSelections(initialSelections);
  }, [groups, modalState.isOpen, preferences]);

  const handleVisibilityChange = async (
    item: CustomizableNavItemGroup['items'][number],
    section: string,
    visibility: SideNavVisibility,
  ) => {
    const previousValue = selectionsRef.current[item.id] ?? item.effectiveVisibility;
    const nextSelections = { ...selectionsRef.current, [item.id]: visibility };
    const saveId = saveIdRef.current + 1;

    saveIdRef.current = saveId;
    selectionsRef.current = nextSelections;
    setSelections(nextSelections);
    onPreferenceChange({ itemId: item.id, section, previousValue, newValue: visibility });

    try {
      const savedPreferences = await onSave(
        buildSideNavPreferences(preferencesRef.current, groups, nextSelections, environmentId),
      );

      if (saveIdRef.current === saveId) {
        preferencesRef.current = savedPreferences;
      }
    } catch {
      return;
    }
  };

  return (
    <Modal onClose={onClose} size="sm" state={modalState} title="Customize sidebar">
      <ModalBody>
        <div className={styles.groups}>
          {groups.map((group) => (
            <Box as="section" className={styles.group} key={group.title}>
              <TextLabel as="h3" className={styles.groupTitle} color="placeholder">
                {group.title}
              </TextLabel>

              <Card className={styles.groupPanel}>
                {group.items.map((item) => {
                  const visibility = selections[item.id] ?? item.effectiveVisibility;
                  const isHidden = visibility === 'hide';

                  return (
                    <Box className={styles.row} key={item.id}>
                      <InlineStack alignBlock="center" className={styles.itemContent} gap="100">
                        {item.iconName != null ? (
                          <Icon color={isHidden ? 'disabled' : 'navigation'} name={item.iconName} />
                        ) : null}
                        <Text
                          className={styles.itemLabel}
                          color={isHidden ? 'disabled' : undefined}
                          text="product-p"
                          truncate={true}
                        >
                          {item.label}
                        </Text>
                      </InlineStack>

                      <Select
                        ariaLabel={`${item.label} visibility`}
                        className={styles.select}
                        onChange={(value) => {
                          if (value !== 'show' && value !== 'hide') {
                            return;
                          }

                          if (value === visibility) {
                            return;
                          }

                          void handleVisibilityChange(item, group.title, value);
                        }}
                        options={SIDE_NAV_VISIBILITY_OPTIONS}
                        value={visibility}
                      />
                    </Box>
                  );
                })}
              </Card>
            </Box>
          ))}
        </div>
      </ModalBody>
    </Modal>
  );
}

function getInitialSelections(groups: CustomizableNavItemGroup[]): Record<string, SideNavVisibility> {
  return Object.fromEntries(groups.flatMap((group) => group.items.map((item) => [item.id, item.effectiveVisibility])));
}
